
import React from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { Calendar, ChefHat, LayoutGrid, Truck, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { isToday } from 'date-fns';

const HomePage: React.FC = () => {
  const { tables, orders, kitchenOrders, deliveryOrders, currentUser } = useApp();
  
  // Obter o tipo de empresa do localStorage
  const empresaTipo = localStorage.getItem('empresa_tipo') || 'Restaurante';
  const isRestaurante = empresaTipo === 'Restaurante';
  
  // Filtramos as permissões com base no papel do usuário e tipo de empresa
  const isAdmin = currentUser?.role === 'admin';
  const isManager = currentUser?.isManager === true;
  const isAdminOrManager = isAdmin || isManager;
  
  // Mostra opções específicas de restaurante apenas se o tipo da empresa for "Restaurante"
  const showTables = isRestaurante && ['admin', 'waiter'].includes(currentUser?.role || '');
  const showKitchen = isRestaurante && ['admin', 'kitchen'].includes(currentUser?.role || '');
  const showDelivery = ['admin', 'delivery'].includes(currentUser?.role || '');
  
  // Filtramos as mesas do dia atual
  const todayTables = tables.filter(table => {
    if (table.createdAt && isToday(new Date(table.createdAt))) {
      return true;
    }
    
    if (table.timeOpened && isToday(new Date(table.timeOpened))) {
      return true;
    }
    
    if (table.cancelDate && isToday(new Date(table.cancelDate))) {
      return true;
    }
    
    if (table.orders && table.orders.some(order => 
      order.createdAt && isToday(new Date(order.createdAt))
    )) {
      return true;
    }
    
    return false;
  });
  
  const occupiedTables = todayTables.filter(table => table.status === 'occupied').length;
  const activeOrders = orders.filter(order => order.status === 'open').length;
  
  const todayDate = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  // Ordenamos as mesas ocupadas por número
  const sortedOccupiedTables = tables
    .filter(table => table.status === 'occupied')
    .sort((a, b) => a.number - b.number);
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col space-y-2">
        <p className="text-muted-foreground">
          {todayDate.charAt(0).toUpperCase() + todayDate.slice(1)}
        </p>
        <h1 className="text-3xl font-bold tracking-tight">Bem-vindo ao RSP Cloud App</h1>
        <p className="text-muted-foreground">
          {currentUser?.name} - {getRole(currentUser?.role)}
        </p>
      </div>
      
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {showTables && (
          <Card className="table-item">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Mesas de Hoje</CardTitle>
              <LayoutGrid className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{occupiedTables} / {todayTables.length}</div>
              <p className="text-xs text-muted-foreground">
                {todayTables.length > 0 
                  ? `${((occupiedTables / todayTables.length) * 100).toFixed(0)}% de ocupação`
                  : 'Nenhuma mesa ocupada hoje'}
              </p>
            </CardContent>
          </Card>
        )}
        
        {showTables && (
          <Card className="table-item">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pedidos Ativos</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeOrders}</div>
              <p className="text-xs text-muted-foreground">
                Pedidos em andamento
              </p>
            </CardContent>
          </Card>
        )}
        
        {showKitchen && (
          <Card className="table-item">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Itens na Cozinha</CardTitle>
              <ChefHat className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {kitchenOrders.reduce((acc, curr) => acc + curr.items.length, 0)}
              </div>
              <p className="text-xs text-muted-foreground">
                Itens em preparo
              </p>
            </CardContent>
          </Card>
        )}
        
        {showDelivery && (
          <Card className="table-item">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Entregas</CardTitle>
              <Truck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{deliveryOrders.length}</div>
              <p className="text-xs text-muted-foreground">
                Pedidos para entrega
              </p>
            </CardContent>
          </Card>
        )}
      </div>
      
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Acesso Rápido</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            {showTables && (
              <Button asChild variant="outline" className="h-20 flex flex-col w-full">
                <Link to="/tables">
                  <LayoutGrid className="h-6 w-6 mb-1" />
                  <span>Gerenciar Mesas</span>
                </Link>
              </Button>
            )}
            
            {showKitchen && (
              <Button asChild variant="outline" className="h-20 flex flex-col w-full">
                <Link to="/kitchen">
                  <ChefHat className="h-6 w-6 mb-1" />
                  <span>Cozinha</span>
                </Link>
              </Button>
            )}
            
            {showDelivery && (
              <Button asChild variant="outline" className="h-20 flex flex-col w-full">
                <Link to="/delivery">
                  <Truck className="h-6 w-6 mb-1" />
                  <span>Entregas</span>
                </Link>
              </Button>
            )}
            
            {isAdminOrManager && (
              <Button asChild variant="outline" className="h-20 flex flex-col w-full">
                <Link to="/settings">
                  <Calendar className="h-6 w-6 mb-1" />
                  <span>Acesso Administrativo</span>
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
        
        {showTables && (
          <Card className="col-span-1">
            <CardHeader>
              <CardTitle>Mesas Recentes</CardTitle>
            </CardHeader>
            <CardContent className="px-0">
              <div className="space-y-2">
                {sortedOccupiedTables
                  .slice(0, 5)
                  .map(table => (
                    <Link key={table.id} to={`/tables/${table.id}`}>
                      <div className="flex items-center justify-between px-6 py-2 hover:bg-accent transition-colors">
                        <div className="flex items-center space-x-4">
                          <div className="bg-primary/10 rounded-full h-8 w-8 flex items-center justify-center">
                            <span className="text-sm font-medium">{table.number}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium">Mesa {table.number}</p>
                            <p className="text-xs text-muted-foreground">
                              {table.orders.filter(o => o.status === 'open').length} pedidos ativos
                            </p>
                          </div>
                        </div>
                        <div className="text-sm font-medium">
                          {table.orders
                            .filter(o => o.status === 'open')
                            .reduce((acc, curr) => acc + curr.totalAmount, 0)
                            .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </div>
                      </div>
                    </Link>
                  ))}
                  
                {tables.filter(table => table.status === 'occupied').length === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    Nenhuma mesa ocupada no momento
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {showDelivery && !showTables && (
          <Card className="col-span-1">
            <CardHeader>
              <CardTitle>Entregas Pendentes</CardTitle>
            </CardHeader>
            <CardContent className="px-0">
              <div className="space-y-2">
                {deliveryOrders.slice(0, 5).map(order => (
                  <Link key={order.id} to="/delivery">
                    <div className="flex items-center justify-between px-6 py-2 hover:bg-accent transition-colors">
                      <div className="flex items-center space-x-4">
                        <div className="bg-primary/10 rounded-full h-8 w-8 flex items-center justify-center">
                          <Truck className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Pedido #{order.id.substring(0, 8)}</p>
                          <p className="text-xs text-muted-foreground">
                            {order.items.length} itens
                          </p>
                        </div>
                      </div>
                      <div className="text-sm font-medium">
                        {order.totalAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </div>
                    </div>
                  </Link>
                ))}
                
                {deliveryOrders.length === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    Nenhuma entrega pendente no momento
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {showKitchen && !showTables && !showDelivery && (
          <Card className="col-span-1">
            <CardHeader>
              <CardTitle>Pedidos na Cozinha</CardTitle>
            </CardHeader>
            <CardContent className="px-0">
              <div className="space-y-2">
                {kitchenOrders.slice(0, 5).map(ko => (
                  <Link key={ko.order.id} to="/kitchen">
                    <div className="flex items-center justify-between px-6 py-2 hover:bg-accent transition-colors">
                      <div className="flex items-center space-x-4">
                        <div className="bg-primary/10 rounded-full h-8 w-8 flex items-center justify-center">
                          <ChefHat className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">Pedido #{ko.order.id.substring(0, 8)}</p>
                          <p className="text-xs text-muted-foreground">
                            {ko.items.length} itens para preparar
                          </p>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
                
                {kitchenOrders.length === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    Nenhum pedido na cozinha no momento
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Função auxiliar para obter o nome do papel em português
const getRole = (role?: string): string => {
  switch (role) {
    case 'admin':
      return 'Administrador';
    case 'waiter':
      return 'Garçom';
    case 'kitchen':
      return 'Cozinheiro';
    case 'delivery':
      return 'Entregador';
    default:
      return 'Usuário';
  }
};

export default HomePage;
